import React, { useState } from "react"
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputAdornment,
  MenuItem,
  Rating,
  Select,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material"
import {
  StyledOutlinedTextField
} from '../../Infrastructure/Theme/styledComponents';
import FilterListIcon from "@mui/icons-material/FilterList"
import ExpandMore from '@mui/icons-material/ExpandMore';
import Search from '@mui/icons-material/Search';
import Grid from "@mui/material/Unstable_Grid2/Grid2"
import { useSelector } from "react-redux"
import { Loader } from "../Common/Loader/loader"
import './style.css'
import { CourseCard } from "../Common/CourseCard/courseCard"
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import { useNavigate } from "react-router-dom"
import { trackEvent } from "../../Infrastructure/Utilities/analytics"
import AnalyticsConstants from "../../Infrastructure/Constants/AnalyticsContants"
import { COURSE_CATEGORIES } from "../../Infrastructure/Constants/courseCategories"
import { SHOW_RATINGS } from "../../Infrastructure/Constants/ratingsState";
import { ErrorView } from "../Common/ErrorComponents/errorView";
import useAuthentication from "../../Infrastructure/States/onAuthStateChange";
import paths from "../../Infrastructure/Router/paths";
import { USER_LOGS_CLIENT } from "../../Infrastructure/Constants/logsConstant";
import { safeClientLogEvent } from "../../Infrastructure/Utilities/safeClientLogEvent";

export const Courses = () => {
  const ratings = [
    {
      value: 5,
    },
    {
      value: 4,
    },
    {
      value: 3,
    }
  ]
  const theme = useTheme();
  const navigate = useNavigate()
  const {userId} = useAuthentication()
  const [showMore, setShowMore] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"))
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [enableFilter, setEnableFilter] = useState(!isSmallScreen)
  const [selectedValue, setSelectedValue] = useState("newest")
  const [showMoreRatings, setShowMoreRatings] = useState(false)
  const error = useSelector((state) => state.userData.error)
  const activeCoursesError = useSelector((state) => state.userData.activeCoursesError)
  const loading = useSelector((state) => state.userData.loading)
  const coursesData = useSelector((state) => state.userData.coursesData)
  const visibleCategories = showMore ? COURSE_CATEGORIES : COURSE_CATEGORIES.slice(0, 5);
  const visibleRatings = showMoreRatings ? ratings : ratings.slice(0, 5);
  const toggleShowMore = () => {
    setShowMore(!showMore);
  };
  const toggleShowMoreRatings = () => {
    setShowMoreRatings(!showMoreRatings);
  }
  const handleCategoryChange = (label) => {
    if (selectedCategories.includes(label)) {
      setSelectedCategories(selectedCategories.filter((category) => category !== label));
    } else {
      setSelectedCategories([...selectedCategories, label]);
    }
    if (isSmallScreen) {
      setEnableFilter(false);
    }
  };
  const handleCardClick = async (courseId, courseDetails, url) => {
    // Navigate to the course page
    navigate(paths.coursesPage.path + `/${url}`);
    // Track the course click event
    try {
      trackEvent(AnalyticsConstants.COURSE_VIEWED, {
        courseId,
        courseUrl: url,
        courseTitle: courseDetails?.basics?.title,
      });
    } catch (trackingError) {
      await safeClientLogEvent({
        ...USER_LOGS_CLIENT.TRACKING_EVENT_FAILED,
        uid: userId || null,
        source: "Courses.jsx",
        emailInfo: null,
        metadata: null,
        error: trackingError?.message || null
      });
    }
  }
  const renderCourseCards = () => {
    if (!coursesData) {
      return [];
    }
    return (
      Object.entries(coursesData)
        .filter(([courseId, course]) => {
          const title = course?.courseDetails?.basics?.title?.toLowerCase() || ""
          const subtitle = course?.courseDetails?.basics?.subTitle?.toLowerCase() || ""
          const firstName = course?.user?.account?.first_name?.toLowerCase() || ""
          const lastName = course?.user?.account?.last_name?.toLowerCase() || ""
          const category = course?.courseDetails?.basics?.category
          const isVisible = course?.visible === true
          const searchWords = searchTerm
            .trim()
            .toLowerCase()
            .split(/\s+/)
            .filter(Boolean)
          const matchesSearch =
            searchWords.length === 0 ||
            searchWords.some(word =>
              title.includes(word) ||
              subtitle.includes(word) ||
              firstName.includes(word) ||
              lastName.includes(word)
            )
          const matchesCategory =
            selectedCategories.length === 0 ||
            selectedCategories.includes(category)
          return matchesSearch && matchesCategory && isVisible
        })
        .sort(([a], [b]) => {
          if (selectedValue === 'newest') {
            return b - a;
          } else if (selectedValue === 'oldest') {
            return a - b;
          } else {
            return 0;
          }
        })
        .map(([courseId, { courseDetails, user, url }]) => {
          return (
            <CourseCard
              key={courseId}
              onClick={() => handleCardClick(courseId, courseDetails, url)}
              user={user} courseDetails={courseDetails} enableFilter={enableFilter} courseId={courseId} stars={5} reviews={"1"} />
          );
        })
    );
  };
  return (
    <Box
      sx={{
        minHeight: "100vh",
        maxWidth: '1500px',
        px: { xs: 2, md: 5 },
        py: 3,
        mx: 'auto'
      }}
    >
      {activeCoursesError ? (
       <Box sx={{mt:20}}>
         <ErrorView onRetry={() => window.location.reload()} />
       </Box> 
      ) : (
        <>
          <Box sx={{ display: "flex", flexDirection: { md: 'row', xs: 'column' }, width: "100%" }} py={3}>
            <Box width={{ xs: '100%', md: '28%' }} display={'flex'} justifyContent={'space-between'} gap={5}>
              <Button
                color="secondary"
                sx={{
                  paddingX: "30px",
                  paddingY: "27px",
                  textTransform: "capitalize",
                }}
                onClick={() => setEnableFilter(prevState => !prevState)}
                startIcon={<FilterListIcon fontSize="30px" />}
              >
                Filter
              </Button>
              <FormControl fullWidth sx={{
                px: 0,
                width: '130px',
                position: 'relative',
                "& .MuiOutlinedInput-root": {
                  borderRadius: "30px !important"
                }
              }}>
                <p
                  style={{
                    fontSize: "11px", fontWeight: "bold",
                    position: 'absolute', top: 10, left: "20px",
                  }}
                >Sort by</p>
                <Select
                  value={selectedValue}
                  onChange={(e) => setSelectedValue(e.target.value)}
                  sx={{
                    borderRadius: '0px',
                    height: '56px',
                    display: 'flex',
                    alignItems: 'flex-end',
                    px: "10px",
                    "& .MuiSelect-select.MuiInputBase-input.MuiOutlinedInput-input": {
                      padding: '25px 0px 5px 10px',
                    },
                    "& .MuiSvgIcon-root.MuiSelect-icon": {
                      position: 'absolute',
                      right: '10px',
                      top: '25px',
                    },
                    "& .MuiInputBase-root.MuiOutlinedInput-root": {
                      borderRadius: '100px',
                    }
                  }}
                >
                  <MenuItem value="newest">Newest</MenuItem>
                  <MenuItem value="oldest">Oldest</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box width={{ xs: '100%', md: "73%" }} ml={{ sm: 5 }} mt={{ xs: 2, sm: 0 }}>
              <StyledOutlinedTextField
                id="input-with-icon-textfield"
                variant="outlined"
                sx={{ width: '100%', float: 'right',
                  "& .MuiOutlinedInput-notchedOutline": {
                   borderRadius: "30px !important",
                  },
                }}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Box>
          <Grid container>
            {
              enableFilter ?
                <Grid xs={12} lg={3.24} height={{ lg: '80vh' }} overflow={'scroll'}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "10px",
                      width: "100%",
                    }}
                  >
                    <Accordion
                      sx={{
                        borderTop: '2px solid lightgray',
                        boxShadow: 'none',
                        borderRadius: '0px',
                        display: SHOW_RATINGS ? "block" : "none"
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMore fontSize="small" />}
                        aria-controls="panel1a-content"
                        id="panel1a-header"
                        onClick={() => setShowMoreRatings(false)}
                      >
                        <Typography variant="h6" fontSize={17}>Ratings</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <FormGroup>
                          {visibleRatings.map(({ value }, index) => (
                            <FormControlLabel
                              key={index}
                              value={value}
                              control={<Checkbox />}
                              label={<Box display={'flex'} alignItems={'center'}>
                                <Rating name="read-only" value={value} readOnly />
                                <div> {value} & Up </div>
                              </Box>}
                            />
                          ))}
                        </FormGroup>
                        {visibleRatings.length > 5 && (
                          <Typography variant="body1" color="primary" onClick={toggleShowMoreRatings} sx={{ cursor: 'pointer' }}>
                            {showMoreRatings ? 'Show less' : 'Show more'}
                            {showMoreRatings ? <ArrowDropUpIcon fontSize="small" /> : <ArrowDropDownIcon fontSize="small" />}
                          </Typography>
                        )}
                      </AccordionDetails>
                    </Accordion>
                    <Accordion 
                    defaultExpanded
                    sx={{
                      borderTop: '2px solid lightgray',
                      boxShadow: 'none',
                      borderRadius: '0px'
                    }}>
                      <AccordionSummary
                        expandIcon={<ExpandMore fontSize="small" />}
                        aria-controls="panel1a-content"
                        id="panel1a-header"
                        onClick={() => setShowMore(false)}
                      >
                        <Typography variant="h6" fontSize={17}>Categories</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <FormGroup>
                          {visibleCategories.map(({ name, label }, index) => (
                            <FormControlLabel
                              key={index}
                              control={<Checkbox checked={selectedCategories.includes(label)} onChange={() => handleCategoryChange(label)} />}
                              value={label}
                              label={label}
                            />
                          ))}
                        </FormGroup>
                        {COURSE_CATEGORIES.length > 5 && (
                          <Typography variant="body1" color="primary" onClick={toggleShowMore} sx={{ cursor: 'pointer' }}>
                            {showMore ? 'Show less' : 'Show more'}
                            {showMore ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
                          </Typography>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  </Box>
                </Grid> : ""}
            <Grid sm={12} lg={enableFilter ? 8.76 : 12} pl={{ lg: enableFilter ? 5 : 0 }} pt={{ xs: 5, lg: 0 }} width={'100%'} height={loading || loading ? 'auto' : '70vh'} overflow={'scroll'}>
              {
                loading ? <Box height={'80vh'}><Loader /></Box> :
                  renderCourseCards()?.length > 0 ? renderCourseCards() :
                    <Box height={'50vh'} width={'100%'} textAlign={'center'} display={'flex'} alignItems={'center'} justifyContent={'center'}>
                      <Typography textAlign={'center'} variant="body1">{error ? error : "No courses found."}</Typography>
                    </Box>
              }
            </Grid>
          </Grid>
        </>
      )
      }
    </Box >
  )
}